import { useState, useEffect } from "react";

interface User {
  userToken: string;
  userId: string;
  expirationTime?: string;
}

interface SessionDetails {
  semesterId: number;
  sessionId: number;
}

interface Role {
  RoleId: string;
  RoleName: string;
  IsSysAdmin: boolean;
}

const AUTH_STORAGE_KEY = "result-manager-auth";
const ROLES_STORAGE_KEY = "result-manager-roles";
const SESSION_STORAGE_KEY = "session-details";

const getStorageValue = <T,>(key: string, fallback: T): T => {
  try {
    return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback));
  } catch {
    localStorage.removeItem(key);
    return fallback;
  }
};

export const isValidUserSession = (user?: Partial<User>) => {
  return Boolean(user?.userToken && user?.userId);
};

export const hasUserCredentials = (user?: Partial<User>): user is User => {
  return Boolean(user?.userId && user?.userToken);
};

export const hasSessionDetails = (
  sessionDetails?: Partial<SessionDetails>
): sessionDetails is SessionDetails => {
  return (
    typeof sessionDetails?.sessionId === "number" &&
    typeof sessionDetails?.semesterId === "number"
  );
};

const clearStoredAuth = () => {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem(ROLES_STORAGE_KEY);
  localStorage.removeItem(SESSION_STORAGE_KEY);
};

const useAuth = () => {
  const storedUser = getStorageValue<Partial<User>>(AUTH_STORAGE_KEY, {});
  const initialUser = isValidUserSession(storedUser) ? storedUser : {};

  if (!isValidUserSession(storedUser)) {
    clearStoredAuth();
  }

  const [user, setUser] = useState<Partial<User>>(initialUser);
  const [roles, setRoles] = useState<Role[]>(
    getStorageValue<Role[]>(ROLES_STORAGE_KEY, [])
  );
  const [sessionDetails, setSessionDetails] = useState<Partial<SessionDetails>>(
    getStorageValue<Partial<SessionDetails>>(SESSION_STORAGE_KEY, {})
  );

  useEffect(() => {
    if (!isValidUserSession(user)) {
      clearStoredAuth();
      return;
    }

    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
    localStorage.setItem(ROLES_STORAGE_KEY, JSON.stringify(roles));
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionDetails));
  }, [user, roles, sessionDetails]);

  return { user, setUser, roles, setRoles, sessionDetails, setSessionDetails };
};

export default useAuth;
